import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Slot } from "@radix-ui/react-slot";
import { AnimatePresence, motion } from "framer-motion";
import { cn } from "../../utils/classNames";
import { usePrefersReducedMotion } from "../../hooks/usePrefersReducedMotion";

const DropdownMenuContext = React.createContext(null);

const getMenuItems = (container) => {
  if (!container) {
    return [];
  }
  return Array.from(container.querySelectorAll(
      '[role="menuitem"], [role="menuitemradio"], [role="menuitemcheckbox"]'
    )).filter(
    (item) => item.getAttribute("aria-disabled") !== "true"
  );
};

const DropdownMenu = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);
  const contentRef = useRef(null);
  const focusTargetRef = useRef("selected");

  const closeMenu = useCallback(
    ({ focusTrigger = true } = {}) => {
      // The menu stays mounted for its exit animation; keep it out of the tab order meanwhile.
      contentRef.current?.setAttribute("inert", "");
      setIsOpen(false);
      if (focusTrigger && triggerRef.current) {
        triggerRef.current.focus();
      }
    },
    []
  );

  const openMenu = useCallback((target = "selected") => {
    focusTargetRef.current = target;
    setIsOpen(true);
  }, []);

  const toggleMenu = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!isOpen || !menuRef.current) {
        return;
      }
      if (!menuRef.current.contains(event.target)) {
        closeMenu({ focusTrigger: false });
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [closeMenu, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const id = window.requestAnimationFrame(() => {
      contentRef.current?.removeAttribute("inert");
      const items = getMenuItems(contentRef.current);
      const target = focusTargetRef.current;
      focusTargetRef.current = "selected";
      const checked = items.find((item) => item.getAttribute("aria-checked") === "true");
      const focusItem = target === "last" ? items[items.length - 1] : checked || items[0];
      focusItem?.focus();
    });
    return () => window.cancelAnimationFrame(id);
  }, [isOpen]);

  const contextValue = useMemo(
    () => ({
      isOpen,
      openMenu,
      closeMenu,
      toggleMenu,
      triggerRef,
      contentRef,
    }),
    [closeMenu, isOpen, openMenu, toggleMenu]
  );

  return (
    <DropdownMenuContext.Provider value={contextValue}>
      <div ref={menuRef} className="relative">
        {children}
      </div>
    </DropdownMenuContext.Provider>
  );
};

const DropdownMenuTrigger = React.forwardRef(
  ({ children, asChild = false, className, onClick, onKeyDown, ...props }, ref) => {
    const context = React.useContext(DropdownMenuContext);

    if (!context) {
      return null;
    }

    const { isOpen, toggleMenu, openMenu, closeMenu, triggerRef } = context;
    const handleKeyDown = (event) => {
      onKeyDown?.(event);
      if (event.defaultPrevented) {
        return;
      }
      if (event.key === "ArrowDown") {
        event.preventDefault();
        openMenu();
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        openMenu("last");
        return;
      }
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        if (isOpen) {
          closeMenu();
        } else {
          openMenu();
        }
      }
    };

    const handleClick = (event) => {
      onClick?.(event);
      if (!event.defaultPrevented) {
        toggleMenu();
      }
    };

    const attachRef = (node) => {
      triggerRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    };

    if (asChild) {
      return (
        <Slot
          ref={attachRef}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          aria-haspopup="menu"
          aria-expanded={isOpen}
          className={className ?? "inline-flex items-center justify-center"}
          {...props}
        >
          {children}
        </Slot>
      );
    }

    return (
      <button
        type="button"
        ref={attachRef}
        className={className ?? "inline-flex items-center justify-center"}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        {...props}
      >
        {children}
      </button>
    );
  }
);

const DropdownMenuContent = React.forwardRef(
  ({ children, align = "start", fitTrigger = false, className, ...props }, ref) => {
    const prefersReducedMotion = usePrefersReducedMotion();
    const context = React.useContext(DropdownMenuContext);

    if (!context) {
      return null;
    }

    const { isOpen, closeMenu, contentRef } = context;

    const attachRef = (node) => {
      contentRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeMenu();
        return;
      }
      if (event.key === "Tab") {
        closeMenu();
        return;
      }

      const items = getMenuItems(contentRef.current);
      if (!items.length) {
        return;
      }

      const currentIndex = items.indexOf(document.activeElement);
      if (event.key === "ArrowDown") {
        event.preventDefault();
        const nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % items.length;
        items[nextIndex].focus();
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        const nextIndex =
          currentIndex === -1 ? items.length - 1 : (currentIndex - 1 + items.length) % items.length;
        items[nextIndex].focus();
      } else if (event.key === "Home") {
        event.preventDefault();
        items[0].focus();
      } else if (event.key === "End") {
        event.preventDefault();
        items[items.length - 1].focus();
      }
    };

    return (
      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={attachRef}
            initial={prefersReducedMotion ? false : { opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -4 }}
            transition={{ duration: prefersReducedMotion ? 0 : 0.15 }}
            className={cn(
              "absolute z-dropdown mt-2 max-w-xs rounded-md border py-2",
              fitTrigger ? "min-w-full" : "min-w-48",
              "border-accent-gold/45 bg-insight-screen text-foreground shadow-dropdown",
              "max-h-60 overflow-auto",
              align === "end" ? "right-0" : "left-0",
              className
            )}
            role="menu"
            aria-orientation="vertical"
            tabIndex={-1}
            onKeyDown={handleKeyDown}
            {...props}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    );
  }
);

const DropdownMenuItem = React.forwardRef(
  ({ children, onClick, disabled = false, className, ...props }, ref) => {
    const context = React.useContext(DropdownMenuContext);

    if (!context) {
      return null;
    }

    const { closeMenu } = context;
    const selected = props["aria-checked"] === true;

    const handleClick = (event) => {
      if (disabled) {
        event.preventDefault();
        return;
      }
      onClick?.(event);
      if (!event.defaultPrevented) {
        closeMenu();
      }
    };

    return (
      <button
        type="button"
        ref={ref}
        role="menuitem"
        className={cn(
          "w-full flex items-center gap-2 px-4 py-2 text-left",
          "text-body-small font-semibold",
          // Inset focus ring: an outward outline would be clipped by the menu's overflow.
          "focus-visible:-outline-offset-2",
          selected
            ? "bg-primary text-primary-foreground focus-visible:outline-primary-foreground"
            : "hover:bg-muted focus-visible:bg-muted",
          "transition-colors duration-150",
          disabled && "cursor-not-allowed opacity-60",
          className
        )}
        aria-disabled={disabled}
        disabled={disabled}
        onClick={handleClick}
        {...props}
      >
        {children}
      </button>
    );
  }
);

const DropdownMenuSeparator = ({ className }) => (
  <div className={cn("my-1 border-t border-border/70", className)} role="separator" />
);

const DropdownMenuLabel = ({ children, className }) => (
  <div
    className={cn(
      "px-4 py-2 text-body-small font-semibold text-muted-foreground",
      className
    )}
    role="presentation"
  >
    {children}
  </div>
);

DropdownMenu.displayName = "DropdownMenu";
DropdownMenuTrigger.displayName = "DropdownMenu.Trigger";
DropdownMenuContent.displayName = "DropdownMenu.Content";
DropdownMenuItem.displayName = "DropdownMenu.Item";
DropdownMenuSeparator.displayName = "DropdownMenu.Separator";
DropdownMenuLabel.displayName = "DropdownMenu.Label";

DropdownMenu.propTypes = {
  children: PropTypes.node,
};

DropdownMenuTrigger.propTypes = {
  children: PropTypes.node.isRequired,
  asChild: PropTypes.bool,
  className: PropTypes.string,
  onClick: PropTypes.func,
  onKeyDown: PropTypes.func,
};

DropdownMenuContent.propTypes = {
  children: PropTypes.node,
  align: PropTypes.oneOf(["start", "end"]),
  fitTrigger: PropTypes.bool,
  className: PropTypes.string,
};

DropdownMenuItem.propTypes = {
  children: PropTypes.node,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  "aria-checked": PropTypes.bool,
};

DropdownMenuSeparator.propTypes = {
  className: PropTypes.string,
};

DropdownMenuLabel.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

DropdownMenu.Trigger = DropdownMenuTrigger;
DropdownMenu.Content = DropdownMenuContent;
DropdownMenu.Item = DropdownMenuItem;
DropdownMenu.Separator = DropdownMenuSeparator;
DropdownMenu.Label = DropdownMenuLabel;

export default DropdownMenu;
